import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChartLineUp, Stack, ArrowRight } from "@phosphor-icons/react";
import { fetchSectors, fetchSectorDetail } from "../lib/api";
import SEO from "../components/SEO";

const labelColor = (s) =>
  s >= 60 ? "#34C759" : s <= 40 ? "#FF3B30" : "#FFCC00";

export function SectorsIndex() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSectors("Global").then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  const sectors = data?.sectors || [];

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="sectors-page">
      <SEO
        title="Stock Market Sectors — Live Sentiment by Industry"
        path="/sectors"
        description="Live sentiment scores by stock market sector — Technology, Financials, Energy, Healthcare, Consumer and more. Click any sector to see top constituents ranked by AI composite sentiment."
        keywords={["sector sentiment", "stock sector heatmap", "technology sector outlook", "energy sector sentiment", "best sectors today"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Live sector sentiment",
          numberOfItems: sectors.length,
          itemListElement: sectors.slice(0, 10).map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://sent-i-meter.com/sector/${s.slug}`,
            name: s.sector,
          })),
        }}
      />
      <SectorsHeader navigate={navigate} title="SECTORS" sub="sentiment by industry" />
      <main className="px-4 md:px-6 py-6 max-w-[1200px] mx-auto">
        <section className="bg-surface border border-white/10 p-5 mb-5">
          <div className="overline">live · composite sentiment 0–100</div>
          <h1 className="heading text-3xl md:text-4xl font-bold mt-1">Sectors</h1>
          <p className="text-sm text-white/70 mt-2 max-w-2xl">
            Each sector's score is the average composite sentiment of its constituents — combining technicals, momentum, news and volatility. Tap any sector for the constituent list.
          </p>
        </section>
        {loading && <div className="overline py-10 text-center">loading sectors…</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sectors-grid">
          {sectors.map((s) => (
            <button
              key={s.slug}
              onClick={() => navigate(`/sector/${s.slug}`)}
              className="text-left bg-surface border border-white/10 hover:border-white/30 p-5 transition-colors duration-150"
              data-testid={`sector-card-${s.slug}`}
            >
              <div className="flex items-baseline justify-between">
                <h3 className="heading text-lg font-semibold tracking-tight">{s.sector}</h3>
                <div className="flex items-baseline gap-2">
                  <span className="mono text-2xl font-bold" style={{ color: labelColor(s.avg_composite) }}>
                    {Math.round(s.avg_composite)}
                  </span>
                  <span className="mono text-[10px] uppercase tracking-[0.14em]" style={{ color: labelColor(s.avg_composite) }}>
                    {s.label}
                  </span>
                </div>
              </div>
              <div className="overline mt-1">{s.count} constituents</div>
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="overline text-bullish">top</div>
                  <div className="text-sm text-white/85 truncate">
                    {(s.top || []).slice(0, 2).map((t) => t.symbol).join(", ")}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline text-bearish">bottom</div>
                  <div className="text-sm text-white/85 truncate">
                    {(s.bottom || []).slice(0, 2).map((t) => t.symbol).join(", ")}
                  </div>
                </div>
                <ArrowRight size={16} weight="bold" className="text-white/40 shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}

export function SectorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSectorDetail(slug).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-obsidian text-white flex items-center justify-center" data-testid="sector-loading">
      <span className="overline">loading sector…</span>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="sector-not-found">
      <SectorsHeader navigate={navigate} title="NOT FOUND" sub="" />
      <main className="px-6 py-12 max-w-md mx-auto text-center">
        <div className="overline mb-3">sector not found</div>
        <button onClick={() => navigate("/sectors")} className="px-3 py-1.5 bg-white text-black mono text-[10px] uppercase tracking-[0.16em]">
          See all sectors
        </button>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="sector-detail-page">
      <SEO
        title={`${data.sector} Sector — Live Sentiment & Top Stocks`}
        path={`/sector/${slug}`}
        description={`${data.sector} sector sentiment is currently ${Math.round(data.avg_composite)}/100 (${data.label}). See the top and bottom stocks ranked by Sent-i-meter composite score — combining technicals, news and momentum.`}
        keywords={[`${data.sector.toLowerCase()} sector`, `${data.sector.toLowerCase()} stocks`, `${data.sector.toLowerCase()} sentiment`, "sector outlook", "stock screener"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `${data.sector} sector constituents`,
          numberOfItems: (data.constituents || []).length,
          itemListElement: (data.constituents || []).slice(0, 10).map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://sent-i-meter.com/asset/${encodeURIComponent(it.symbol)}`,
            name: `${it.name} (${it.symbol})`,
          })),
        }}
      />
      <SectorsHeader navigate={navigate} title={data.sector.toUpperCase()} sub={`${data.count} constituents · ${data.label}`} />
      <main className="px-4 md:px-6 py-6 max-w-[1000px] mx-auto">
        <section className="bg-surface border border-white/10 p-5 mb-5">
          <div className="overline">live sector sentiment</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="heading text-4xl font-bold" style={{ color: labelColor(data.avg_composite) }}>
              {Math.round(data.avg_composite)}
            </h1>
            <span className="mono text-sm" style={{ color: labelColor(data.avg_composite) }}>
              {data.label} · {data.sector}
            </span>
          </div>
          <p className="text-sm text-white/70 mt-3 max-w-2xl">
            Average composite sentiment across {data.count} constituent stocks in the {data.sector} sector. Click any stock for full breakdown, news, and forecast.
          </p>
        </section>

        <section className="bg-surface border border-white/10 divide-y divide-white/5" data-testid="sector-constituents">
          {(data.constituents || []).map((it, i) => (
            <button
              key={it.symbol}
              onClick={() => navigate(`/asset/${encodeURIComponent(it.symbol)}`)}
              className="w-full text-left px-5 py-3 flex items-center gap-4 hover:bg-white/[0.03] transition-colors duration-150"
              data-testid={`sector-stock-${it.symbol}`}
            >
              <span className="mono text-xs text-white/40 w-6 text-right">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="heading text-sm font-semibold">{it.symbol}</span>
                  <span className="text-sm text-white/70 truncate">{it.name}</span>
                </div>
              </div>
              {it.change_pct != null && (
                <span className="mono text-xs" style={{ color: it.change_pct >= 0 ? "#34C759" : "#FF3B30" }}>
                  {it.change_pct >= 0 ? "+" : ""}{it.change_pct.toFixed(2)}%
                </span>
              )}
              <span className="mono text-sm font-bold" style={{ color: labelColor(it.composite), minWidth: 40, textAlign: "right" }}>
                {Math.round(it.composite)}
              </span>
            </button>
          ))}
        </section>
      </main>
    </div>
  );
}

function SectorsHeader({ navigate, title, sub }) {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white">
          <ArrowLeft size={16} weight="bold" />
          <span className="overline">back</span>
        </button>
        <div className="w-px h-6 bg-white/10 mx-2" />
        <button onClick={() => navigate("/")} className="w-8 h-8 bg-white text-black flex items-center justify-center">
          <ChartLineUp size={16} weight="bold" />
        </button>
        <div className="hidden sm:block">
          <div className="heading text-sm font-bold tracking-tight leading-none">{title}</div>
          {sub && <div className="overline mt-0.5 leading-none">{sub}</div>}
        </div>
      </div>
      <Stack size={18} weight="duotone" color="#FFCC00" />
    </header>
  );
}
